import { useCallback, useRef } from "react";
import { useCart } from "../../../../hooks/query";
import { useProject } from "../../../../hooks";
import { LaborSelection } from "./useLaborMasterDataInit";

/**
 * useLaborDatabaseSync
 * --------------------
 * Compares UI state vs DB state and syncs the differences specifically for Labor properties.
 * Identical mechanism to useDatabaseSync, but configured for Labor data payloads.
 */
export function useLaborDatabaseSync() {
    const { project } = useProject();
    const { useCreate, useUpdate, useDelete } = useCart();

    const createSvc = useCreate();
    const updateSvc = useUpdate();
    const deleteSvc = useDelete();

    // Memory of what the database currently looks like
    const dbStateRef = useRef<Record<string, any>>({});

    // ─────────────────────────────────────────────────────────────────────────
    // STEP 1: Called once on data load.
    // Seeds our memory with the data that came from the database.
    // ─────────────────────────────────────────────────────────────────────────
    const initializeDbState = useCallback((dataFromDatabase: Record<string, any>) => {
        dbStateRef.current = { ...dataFromDatabase };
    }, []);

    // ─────────────────────────────────────────────────────────────────────────
    // STEP 2: Called every time selectedValues changes via debouncer.
    // Finds what changed and syncs it.
    // ─────────────────────────────────────────────────────────────────────────
    const syncToDatabase = useCallback(
        async (uiState: Record<string, LaborSelection>) => {
            const projectId = project?.id;
            if (!projectId) {
                return;
            }

            // ── Claim the new state IMMEDIATELY ──────────────────────────────────
            const dbState = dbStateRef.current;
            const claimedState: Record<string, any> = {};
            Object.entries(uiState).forEach(([laborServiceId, item]) => {
                claimedState[laborServiceId] = {
                    ...item,
                    id: dbState[laborServiceId]?.id || item.id || (item as any).CartItemId,
                };
            });
            dbStateRef.current = claimedState;

            // ── Compute what needs to change ─────────────────────────────────────
            const toCreate: any[] = [];
            const toUpdate: any[] = [];
            const toDelete: string[] = [];

            Object.entries(uiState).forEach(([laborServiceId, uiItem]) => {
                const dbItem = dbState[laborServiceId];
                const isComplete = (uiItem.quantity || 0) > 0;

                if (!isComplete) return;

                if (!dbItem) {
                    // Not in DB -> Create
                    toCreate.push({ laborServiceId, ...uiItem });
                } else if (uiItem.quantity !== dbItem.quantity) {
                    // In DB, count changed -> Update
                    toUpdate.push({ id: dbItem.id || dbItem.CartItemId, laborServiceId, quantity: uiItem.quantity });
                }
            });

            // Items in DB but gone from UI → DELETE
            Object.keys(dbState).forEach((laborServiceId) => {
                if (!uiState[laborServiceId] && (dbState[laborServiceId]?.id || dbState[laborServiceId]?.CartItemId)) {
                    toDelete.push(dbState[laborServiceId].id || dbState[laborServiceId].CartItemId);
                }
            });

            const hasNoChanges = toCreate.length === 0 && toUpdate.length === 0 && toDelete.length === 0;
            if (hasNoChanges) return;

            console.group("[LaborDatabaseSync] Sync Plan");

            // ── DELETES ───────────────────────────────────────────────────────────
            for (const id of toDelete) {
                if (!id || String(id).startsWith?.("pending_")) continue;
                console.log(`  🗑️  LABOR DELETE id:`, id);
                try {
                    await deleteSvc.mutateAsync({ Id: id });
                } catch (err) {
                    console.error(`  [LaborDatabaseSync] DELETE failed for id ${id}:`, err);
                }
            }

            // ── CREATES ───────────────────────────────────────────────────────────
            if (toCreate.length > 0) {
                console.log(`  ✅ LABOR CREATE (${toCreate.length} items):`, toCreate);
                try {
                    const createdItems: any[] = await createSvc.mutateAsync({
                        ProjectId: projectId,
                        CartItems: toCreate.map((item) => ({
                            LaborServiceId: item.laborServiceId,
                            ItemCount: item.quantity,
                            ProductCategory: "Labor", // API might need this constraint depending on backend
                        })),
                    });

                    // Seed real IDs from the CREATE response into dbStateRef
                    createdItems?.forEach((created: any) => {
                        if (created.LaborServiceId && created.id) {
                            dbStateRef.current[created.LaborServiceId] = {
                                ...uiState[created.LaborServiceId],
                                id: created.id,
                            };
                        }
                    });
                } catch (err) {
                    console.error(`  [LaborDatabaseSync] CREATE failed:`, err);
                }
            }

            // ── UPDATES ───────────────────────────────────────────────────────────
            for (const item of toUpdate) {
                console.log(`  🔄 LABOR UPDATE id:`, item.id, `quantity:`, item.quantity);
                try {
                    await updateSvc.mutateAsync({ Id: item.id, ItemCount: item.quantity });
                } catch (err) {
                    console.error(`  [LaborDatabaseSync] UPDATE failed for id ${item.id}:`, err);
                }
            }

            console.groupEnd();
        },
        [project?.id, createSvc, updateSvc, deleteSvc],
    );

    return {
        initializeDbState,
        syncToDatabase,
    };
}
